import * as THREE from "three";

/** Procedural canvas / DataTextures for gritty industrial sci-fi — readable top-down. */

function makeCanvas(size: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas unavailable");
  return { canvas, ctx };
}

let textureAnisotropy = 4;

function finishTexture(
  canvas: HTMLCanvasElement,
  repeat = 8,
  colorSpace: THREE.ColorSpace = THREE.SRGBColorSpace,
): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = colorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(repeat, repeat);
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = textureAnisotropy;
  tex.needsUpdate = true;
  return tex;
}

/** Applies to subsequently created procedural textures. */
export function setTextureAnisotropy(value: number): void {
  textureAnisotropy = Math.max(1, Math.floor(value));
}

function hash2(x: number, y: number): number {
  let n = x * 374761393 + y * 668265263;
  n = (n ^ (n >>> 13)) * 1274126177;
  return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
}

function wrapInt(i: number, period: number): number {
  const p = Math.max(1, period | 0);
  return ((i % p) + p) % p;
}

function fade(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** Tileable value noise. Period is in noise-cells and must be an integer. */
function valueNoise(x: number, y: number, period: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const u = fade(x - x0);
  const v = fade(y - y0);
  const n00 = hash2(wrapInt(x0, period), wrapInt(y0, period));
  const n10 = hash2(wrapInt(x0 + 1, period), wrapInt(y0, period));
  const n01 = hash2(wrapInt(x0, period), wrapInt(y0 + 1, period));
  const n11 = hash2(wrapInt(x0 + 1, period), wrapInt(y0 + 1, period));
  return n00 + (n10 - n00) * u + (n01 - n00) * v + (n11 - n10 - n01 + n00) * u * v;
}

function fbm(u: number, v: number, basePeriod: number, octaves: number): number {
  let sum = 0;
  let amp = 0.5;
  let norm = 0;
  let freq = 1;
  for (let o = 0; o < octaves; o += 1) {
    const period = basePeriod * freq;
    sum += amp * valueNoise(u * period, v * period, period);
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return sum / norm;
}

/**
 * One stable dirt/rock albedo. Same landform for every biome — callers tint
 * with material.color instead of rebuilding stamps.
 */
export function createTerrainTexture(
  groundHex = 0x5a5348,
  accentHex = 0x7a6a52,
  roughnessBias = 0.55,
): { map: THREE.CanvasTexture; roughnessMap: THREE.CanvasTexture } {
  const size = 512;
  const { canvas, ctx } = makeCanvas(size);
  const { canvas: roughCanvas, ctx: roughCtx } = makeCanvas(size);
  const g = hexToRgb(groundHex);
  const a = hexToRgb(accentHex);
  const img = ctx.createImageData(size, size);
  const rough = roughCtx.createImageData(size, size);

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const u = x / size;
      const v = y / size;
      const land = fbm(u, v, 5, 5);
      const grit = fbm(u + 0.37, v + 0.61, 19, 3);
      const channel = 1 - Math.abs(fbm(u + 0.18, v + 0.09, 7, 4) * 2 - 1);
      const t = land * 0.62 + channel * 0.22 + grit * 0.16;
      const i = (y * size + x) * 4;
      const dust = (grit - 0.5) * 22;
      img.data[i] = clampByte(g.r + (a.r - g.r) * t + dust);
      img.data[i + 1] = clampByte(g.g + (a.g - g.g) * t * 0.92 + dust * 0.85);
      img.data[i + 2] = clampByte(g.b + (a.b - g.b) * t * 0.78 + dust * 0.7);
      img.data[i + 3] = 255;
      const roughV = clampByte(118 + roughnessBias * 70 + land * 40 + grit * 35);
      rough.data[i] = roughV;
      rough.data[i + 1] = roughV;
      rough.data[i + 2] = roughV;
      rough.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  roughCtx.putImageData(rough, 0, 0);
  return {
    map: finishTexture(canvas, 6),
    roughnessMap: finishTexture(roughCanvas, 6, THREE.NoColorSpace),
  };
}

/** Scarred metal plate for ships, carrier hull, props. */
export function createMetalPlateTexture(baseHex = 0x6a7076): THREE.CanvasTexture {
  return createMetalPbrMaps(baseHex).map;
}

export type MetalPbrMaps = {
  map: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
  metalnessMap: THREE.CanvasTexture;
};

/** Scarred metal albedo plus non-color roughness and metalness masks. */
export function createMetalPbrMaps(baseHex = 0x6a7076): MetalPbrMaps {
  const size = 128;
  const { canvas, ctx } = makeCanvas(size);
  const { canvas: roughCanvas, ctx: roughCtx } = makeCanvas(size);
  const { canvas: metalCanvas, ctx: metalCtx } = makeCanvas(size);
  const c = hexToRgb(baseHex);
  ctx.fillStyle = `rgb(${c.r},${c.g},${c.b})`;
  ctx.fillRect(0, 0, size, size);

  for (let y = 0; y < size; y += 8) {
    for (let x = 0; x < size; x += 8) {
      const n = hash2(x, y);
      ctx.fillStyle = `rgba(0,0,0,${0.08 + n * 0.14})`;
      ctx.fillRect(x, y, 7, 7);
      if (n > 0.72) {
        ctx.fillStyle = `rgba(180,140,90,${0.12 + n * 0.1})`;
        ctx.fillRect(x + 1, y + 1, 3, 2);
      }
    }
  }

  // Rivet rows
  ctx.fillStyle = "rgba(30,28,26,0.55)";
  for (let y = 4; y < size; y += 16) {
    for (let x = 4; x < size; x += 16) {
      ctx.beginPath();
      ctx.arc(x, y, 1.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  const rough = roughCtx.createImageData(size, size);
  const metalness = metalCtx.createImageData(size, size);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const i = (y * size + x) * 4;
      const n = hash2(x, y);
      const plateEdge = x % 16 < 1 || y % 16 < 1;
      const roughness = clampByte(105 + n * 78 + (plateEdge ? 35 : 0));
      const metal = clampByte(205 - n * 32 - (plateEdge ? 48 : 0));
      rough.data[i] = roughness;
      rough.data[i + 1] = roughness;
      rough.data[i + 2] = roughness;
      rough.data[i + 3] = 255;
      metalness.data[i] = metal;
      metalness.data[i + 1] = metal;
      metalness.data[i + 2] = metal;
      metalness.data[i + 3] = 255;
    }
  }
  roughCtx.putImageData(rough, 0, 0);
  metalCtx.putImageData(metalness, 0, 0);

  return {
    map: finishTexture(canvas, 2),
    roughnessMap: finishTexture(roughCanvas, 2, THREE.NoColorSpace),
    metalnessMap: finishTexture(metalCanvas, 2, THREE.NoColorSpace),
  };
}

/** Dark industrial panel for bunkers / towers. */
export function createConcreteTexture(baseHex = 0x3a3834): THREE.CanvasTexture {
  const size = 128;
  const { canvas, ctx } = makeCanvas(size);
  const c = hexToRgb(baseHex);
  ctx.fillStyle = `rgb(${c.r},${c.g},${c.b})`;
  ctx.fillRect(0, 0, size, size);
  const img = ctx.getImageData(0, 0, size, size);
  for (let i = 0; i < img.data.length; i += 4) {
    const n = (hash2(i % size, (i / 4 / size) | 0) - 0.5) * 36;
    img.data[i] = clampByte(img.data[i]! + n);
    img.data[i + 1] = clampByte(img.data[i + 1]! + n * 0.95);
    img.data[i + 2] = clampByte(img.data[i + 2]! + n * 0.85);
  }
  ctx.putImageData(img, 0, 0);
  ctx.strokeStyle = "rgba(0,0,0,0.25)";
  ctx.lineWidth = 2;
  ctx.strokeRect(2, 2, size - 4, size - 4);
  ctx.beginPath();
  ctx.moveTo(0, size * 0.5);
  ctx.lineTo(size, size * 0.5);
  ctx.stroke();
  return finishTexture(canvas, 1);
}

export function createEnemyHullTexture(elite: boolean): THREE.CanvasTexture {
  return createMetalPlateTexture(elite ? 0xa88840 : 0x7a4040);
}

function hexToRgb(hex: number): { r: number; g: number; b: number } {
  return {
    r: (hex >> 16) & 0xff,
    g: (hex >> 8) & 0xff,
    b: hex & 0xff,
  };
}

function clampByte(v: number): number {
  return Math.min(255, Math.max(0, Math.round(v)));
}
